import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { polyLine2ByTwoPoints, polyLine2EdgeEval } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";

// handles cases where point lies outside of polyline and non-rectangular polylines with more than two intersections

export function normalizePointToPolyLine2(
  outline: PolyLine2,
  x: number,
  y: number,
  rayXDir: number,
  rayYDir: number,
  offset: number,
  offsetNormalized = true,
  reverse = false,
): Vec2 | null {
  const ints = intersectRay2PolyLine2(outline, x, y, rayXDir, rayYDir);
  ray2PolyLine2IntersectionMakeUnique(ints);

  if (classifyContainmentVec2PolyLine2(x, y, outline) === Vec2PolyLine2ContainmentClass.INSIDE) {
    const positiveInts: Ray2PolyLine2Intersection[] = [];
    const negativeInts: Ray2PolyLine2Intersection[] = [];
    ints.forEach((x) => {
      if (x.t < 0) {
        negativeInts.push(x);
      } else {
        positiveInts.push(x);
      }
    });

    const lastNegative = negativeInts[negativeInts.length - 1];
    const firstPositive = positiveInts[0];

    if (lastNegative === undefined || firstPositive === undefined) {
      return null;
    }

    const { x: int1x, y: int1y } = lastNegative;
    const { x: int2x, y: int2y } = firstPositive;

    return polyLine2EdgeEval(
      reverse ? polyLine2ByTwoPoints(int2x, int2y, int1x, int1y) : polyLine2ByTwoPoints(int1x, int1y, int2x, int2y),
      0,
      offset,
      offsetNormalized,
    );
  } else {
    const positiveInts: Ray2PolyLine2Intersection[] = [];
    const negativeInts: Ray2PolyLine2Intersection[] = [];
    ints.forEach((x) => {
      if (x.t < 0) {
        negativeInts.push(x);
      } else {
        positiveInts.push(x);
      }
    });

    const firstNegativeInt = negativeInts.length > 0 ? negativeInts[negativeInts.length - 1] : undefined;
    const firstPositiveInt = positiveInts.length > 0 ? positiveInts[0] : undefined;

    if (firstNegativeInt === undefined && firstPositiveInt === undefined) {
      return null;
    }

    if (firstPositiveInt === undefined) {
      const { x: n2x, y: n2y } = negativeInts[negativeInts.length - 1];
      if (negativeInts.length > 2) {
        const { x: n1x, y: n1y } = negativeInts[negativeInts.length - 2];
        return polyLine2EdgeEval(
          reverse ? polyLine2ByTwoPoints(n2x, n2y, n1x, n1y) : polyLine2ByTwoPoints(n1x, n1y, n2x, n2y),
          0,
          offset,
          offsetNormalized,
        );
      } else {
        return [n2x, n2y];
      }
    } else if (firstNegativeInt === undefined) {
      const { x: n1x, y: n1y } = positiveInts[0];
      if (positiveInts.length > 2) {
        const { x: n2x, y: n2y } = positiveInts[1];
        return polyLine2EdgeEval(
          reverse ? polyLine2ByTwoPoints(n2x, n2y, n1x, n1y) : polyLine2ByTwoPoints(n1x, n1y, n2x, n2y),
          0,
          offset,
          offsetNormalized,
        );
      } else {
        return [n1x, n1y];
      }
    }

    const positiveIntClosest = firstPositiveInt.t <= Math.abs(firstNegativeInt.t);
    if (positiveIntClosest) {
      const { x: n1x, y: n1y } = positiveInts[0];
      if (positiveInts.length > 2) {
        const { x: n2x, y: n2y } = positiveInts[1];
        return polyLine2EdgeEval(
          reverse ? polyLine2ByTwoPoints(n2x, n2y, n1x, n1y) : polyLine2ByTwoPoints(n1x, n1y, n2x, n2y),
          0,
          offset,
          offsetNormalized,
        );
      } else {
        return [n1x, n1y];
      }
    } else {
      const { x: n2x, y: n2y } = negativeInts[negativeInts.length - 1];
      if (negativeInts.length > 2) {
        const { x: n1x, y: n1y } = negativeInts[negativeInts.length - 2];
        return polyLine2EdgeEval(
          reverse ? polyLine2ByTwoPoints(n2x, n2y, n1x, n1y) : polyLine2ByTwoPoints(n1x, n1y, n2x, n2y),
          0,
          offset,
          offsetNormalized,
        );
      } else {
        return [n2x, n2y];
      }
    }
  }
}
